/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable import/no-webpack-loader-syntax */
import React, { useRef, useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import mapboxgl from 'mapbox-gl/dist/mapbox-gl-csp';
import MapboxWorker from 'worker-loader!mapbox-gl/dist/mapbox-gl-csp-worker';
import {
  projects,
  mapConfig,
  clusters,
  getCategoryColor,
  colors,
  hexToRgb,
} from 'components/constants';

import LegendProjectZone from '../Icons/LegendProjectZone';
import LegendPumpDingley from '../Icons/LegendPumpDingley';
import LegendPumpSM from '../Icons/LegendPumpSM';
import LegendWRP from '../Icons/LegendWRP';
import './Map.scss';

mapboxgl.workerClass = MapboxWorker;
mapboxgl.accessToken =
  'pk.eyJ1IjoiY3JlYXRpdmUtcnBzIiwiYSI6ImNrbnB5aDluMTAzZ3gydm9nMGw4YXJkcmwifQ.LgX741YgMA4wK4AD9TR1tw';

const isPopupOpen = () => document.body.classList.contains('popup-is-open');
const isDingley = false;
const isFB = false;

const activeConsoleLogs = false;

const Map = ({
  projectSelected,
  centerMap,
  onClickMarker,
  onClickLine,
  resetClicked,
}) => {
  const mapContainer = useRef();
  const [map, setMap] = useState(undefined);
  const [markers, setMarkers] = useState([]);

  useEffect(() => {
    setMap(
      new mapboxgl.Map({
        container: mapContainer.current,
        style: mapConfig.style,
        center: mapConfig.centerInitial,
        zoom: mapConfig.zoom,
        maxZoom: mapConfig.maxZoom,
        minZoom: mapConfig.zoom,
      }),
    );
  }, []);

  useEffect(() => {
    if (typeof map === 'undefined') return;
    map.flyTo({
      center: mapConfig.centerInitial,
      zoom: mapConfig.zoom,
    });
    // turn off markers popups
    markers.forEach((m) => {
      const { marker } = m;
      if (marker?.getPopup()?.isOpen()) marker?.togglePopup();
    });
  }, [resetClicked]);

  useEffect(() => {
    const project = projects.find((p) => p.value === projectSelected.project);

    // set opacity line
    if (projectSelected.project) {
      setTimeout(() => {
        document.querySelector('body').classList.add('popup-is-open');
        var myDiv = document.getElementsByClassName('popup-content-source');
        if (myDiv.length) myDiv[0].scrollTop = 0;
      }, 500);
    }

    if (project?.marker?.position) {
      map.flyTo({
        center: project.marker.position,
        zoom: project.zoomOnClick || 13.5,
      });
    }
    if (projectSelected.event === 'leftMenu' && markers.length) {
      markers.forEach((m) => {
        const { value, marker } = m;
        if (value === projectSelected.project) {
          if (!marker.getPopup()?.isOpen()) marker.togglePopup();
        } else if (marker?.getPopup()?.isOpen()) marker?.togglePopup();
      });
    }
  }, [projectSelected]);

  useEffect(() => {
    map?.flyTo({
      center: centerMap,
      zoom: 15,
    });
  }, [centerMap]);

  // Add zoom and rotation controls to the map.
  // map.addControl(new mapboxgl.NavigationControl());
  useEffect(() => {
    if (typeof map === 'undefined') return;

    if (isDingley) {
      map.flyTo({
        center: mapConfig.centerInitialDingley,
        zoom: mapConfig.zoomDingley,
      });
    }

    if (isFB) {
      map.flyTo({
        center: mapConfig.centerInitialFB,
        zoom: mapConfig.zoomFB,
      });
    }

    map.on('zoom', (e) => {
      const zoom = map?.getZoom();

      if (zoom < mapConfig.legendZoomThreshold) {
        document.querySelector('body').classList.remove('show-legends');
      } else {
        if (!document.querySelector('body').classList.contains('show-legends'))
          document.querySelector('body').classList.add('show-legends');
      }
    });

    map.on('click', (e) => console.log(`${e.lngLat.lng}, ${e.lngLat.lat}`));

    map.on('load', () => {
      var nav = new mapboxgl.NavigationControl();
      map.addControl(nav, 'top-right');

      // inspect a cluster on click
      map.on('click', 'clusters', (e) => {
        const features = map.queryRenderedFeatures(e.point, {
          layers: ['clusters'],
        });
        const clusterId = features[0].properties.cluster_id;
        map
          .getSource('counters')
          .getClusterExpansionZoom(clusterId, (err, zoom) => {
            if (err) return;

            map.easeTo({
              center: features[0].geometry.coordinates,
              zoom: 12.5,
            });
          });
      });

      map.on('mouseenter', 'clusters', () => {
        if (activeConsoleLogs) console.log('mouseenter clusters');
        map.getCanvas().style.cursor = 'pointer';
      });
      map.on('mouseleave', 'clusters', () => {
        map.getCanvas().style.cursor = '';
      });

      // add clusters
      clusters.forEach(({ position, color, counter, zoom }) => {
        const el = document.createElement('div');
        el.innerHTML = `<div class="marker-cluster ${color}">
                            <span>${counter}</span>
                          </div>`;

        const markerCluster = new mapboxgl.Marker(el)
          .setLngLat(position)
          .addTo(map);

        markerCluster.getElement().addEventListener('click', (event) => {
          map.flyTo({
            center: position,
            zoom: zoom || 13.4,
          });
        });
      });

      // Lines and Fills
      projects.forEach((p) => {
        // Extract pulsing dot shapes
        const pulsingDotShapes =
          p.shapes?.filter((s) => s.type === 'pulsing-dot') || [];

        const {
          title,
          value,
          category,
          subcategory,
          categoryColor,
          marker,
          color,
          openPopup,
          zoomOnClick,
          skipInteraction = false,
        } = p;

        // Get all non-pulsing-dot shapes for rendering
        const renderableShapes =
          p.shapes?.filter((s) => s.type !== 'pulsing-dot') || [];

        const curColor = getCategoryColor(category, subcategory, categoryColor);

        // Helper to create pulsing dot
        const createPulsingDot = (dotColor, size = 100) => ({
          width: size,
          height: size,
          data: new Uint8Array(size * size * 4),
          onAdd: function () {
            const canvas = document.createElement('canvas');
            canvas.width = this.width;
            canvas.height = this.height;
            this.context = canvas.getContext('2d');
          },
          render: function () {
            const duration = 2000;
            const t = (performance.now() % duration) / duration;
            const radius = (size / 2) * 0.3;
            const outerRadius = (size / 2) * 0.4 * t + radius;
            const context = this.context;
            const colorRgba = hexToRgb(colors[dotColor]);

            context.clearRect(0, 0, this.width, this.height);
            context.beginPath();
            context.arc(
              this.width / 2,
              this.height / 2,
              outerRadius,
              0,
              Math.PI * 2,
            );
            context.fillStyle = `rgba(${colorRgba}, ${1 - t})`;
            context.fill();

            context.beginPath();
            context.arc(
              this.width / 2,
              this.height / 2,
              radius,
              0,
              Math.PI * 2,
            );
            context.fillStyle = `rgba(${colorRgba})`;
            context.strokeStyle = `rgba(${colorRgba}, 0.5)`;
            context.lineWidth = 2 + 2 * (1 - t);
            context.fill();
            context.stroke();

            this.data = context.getImageData(
              0,
              0,
              this.width,
              this.height,
            ).data;
            map.triggerRepaint();
            return true;
          },
        });

        // Process each renderable shape
        renderableShapes.forEach((shape, shapeIndex) => {
          // Use shape's color if defined, otherwise use project's color
          const shapeColor = colors[shape.color] || curColor;
          const shapeData = shape.data;
          const shapeType = shape.type;
          const shapeConfig = shape.config || {};

          // Create unique layer ID for each shape
          const shapeLayerId =
            shapeIndex === 0 ? value : `${value}-shape-${shapeIndex}`;

          // add line
          if (value && shapeData) {
            map.addSource(shapeLayerId, {
              type: 'geojson',
              data: shapeData,
            });

            // Render layers based on renderType
            if (shapeType === 'dashed-line') {
              // Dashed line with invisible hover line
              if (shapeConfig.hasInvisibleHoverLine) {
                map.addLayer({
                  id: shapeLayerId,
                  type: 'line',
                  source: shapeLayerId,
                  layout: { 'line-join': 'round', 'line-cap': 'round' },
                  paint: {
                    'line-color': 'transparent',
                    'line-width': 16,
                    'line-opacity': 0,
                  },
                });
              }
              map.addLayer({
                id: `${shapeLayerId}-main-line`,
                type: 'line',
                source: shapeLayerId,
                layout: { 'line-join': 'round', 'line-cap': 'round' },
                paint: {
                  'line-color': shapeColor,
                  'line-width': shapeConfig.lineWidth || 3,
                  'line-opacity': shapeConfig.defaultOpacity || 0.7,
                  'line-dasharray': shapeConfig.dasharray || [1.5, 2],
                },
              });
            } else if (shapeType === 'polygon-with-line') {
              // Polygon with border line
              map.addLayer({
                id: `${shapeLayerId}-polygon`,
                type: 'fill',
                source: shapeLayerId,
                paint: {
                  'fill-color': shapeColor,
                  'fill-opacity': shapeConfig.polygonOpacity || 0.5,
                },
              });
              if (shapeConfig.useDashedBorder) {
                map.addLayer({
                  id: shapeLayerId,
                  type: 'line',
                  source: shapeLayerId,
                  layout: { 'line-join': 'round', 'line-cap': 'round' },
                  paint: {
                    'line-color': shapeColor,
                    'line-width': 3,
                    'line-opacity': 0.7,
                    'line-dasharray': shapeConfig.dasharray || [1],
                  },
                });
              } else {
                map.addLayer({
                  id: `${shapeLayerId}-main-line`,
                  type: 'line',
                  source: shapeLayerId,
                  layout: { 'line-join': 'round', 'line-cap': 'round' },
                  paint: {
                    'line-color': shapeColor,
                    'line-width': 3,
                    'line-opacity': 0.7,
                  },
                });
              }
            } else if (shapeType === 'pulsing-dot') {
              // Add line layer if project has shapeData (unless skipLineLayer is set)
              if (shapeData && !shapeConfig.skipLineLayer) {
                map.addLayer({
                  id: shapeLayerId,
                  type: 'line',
                  source: shapeLayerId,
                  layout: { 'line-join': 'round', 'line-cap': 'round' },
                  paint: {
                    'line-color': shapeColor,
                    'line-width': 3,
                    'line-opacity': shapeConfig.defaultOpacity || 0.3,
                    'line-dasharray': [1],
                  },
                });
              }
            } else {
              // Default: simple line
              map.addLayer({
                id: shapeLayerId,
                type: 'line',
                source: shapeLayerId,
                layout: { 'line-join': 'round', 'line-cap': 'round' },
                paint: {
                  'line-color': shapeColor,
                  'line-width': shapeConfig.lineWidth || 3,
                  'line-opacity': shapeConfig.defaultOpacity || 0.3,
                  'line-dasharray': shapeConfig.dasharray || [1],
                },
              });
            }

            // when user click on the line
            map.on('click', shapeLayerId, (e) => {
              onClickLine(value);
              if (skipInteraction) return;

              if (zoomOnClick) {
                map.flyTo({
                  center: e.lngLat,
                  zoom: zoomOnClick,
                });
              }
            });

            // when user click on the polygon
            map.on('click', `${shapeLayerId}-polygon`, (e) => {
              if (activeConsoleLogs) console.log('click polygon', value);
              if (skipInteraction) return;
              if (!shapeConfig.disablePolygonClick) onClickLine(value);
              if (zoomOnClick) {
                map.flyTo({
                  center: marker.position,
                  zoom: zoomOnClick,
                });
              }
            });

            // hover line
            map.on('mouseenter', shapeLayerId, (e) => {
              if (activeConsoleLogs) console.log('mouseenter line', value);
              if (skipInteraction) return;
              map.getCanvas().style.cursor = 'pointer';

              if (!isPopupOpen()) {
                map.setPaintProperty(shapeLayerId, 'line-opacity', 1);
              }

              if (shapeType === 'dashed-line' && !isPopupOpen()) {
                map.setPaintProperty(
                  `${shapeLayerId}-main-line`,
                  'line-dasharray',
                  [1],
                );
                map.setPaintProperty(
                  `${shapeLayerId}-main-line`,
                  'line-opacity',
                  1,
                );
              }
            });

            // hover polygon
            map.on('mouseenter', `${shapeLayerId}-polygon`, (e) => {
              if (activeConsoleLogs) console.log('mouseenter polygon', value);
              if (skipInteraction) return;
              if (!shapeConfig.disablePolygonHover) {
                map.getCanvas().style.cursor = 'pointer';
                map.setPaintProperty(
                  `${shapeLayerId}-polygon`,
                  'fill-opacity',
                  1,
                );
              }
            });

            // unhover line
            map.on('mouseleave', shapeLayerId, (e) => {
              if (activeConsoleLogs) console.log('mouseleave line', value);
              if (skipInteraction) return;
              map.getCanvas().style.cursor = '';

              if (!isPopupOpen()) {
                const defaultOpacity = shapeConfig.defaultOpacity || 0.3;
                map.setPaintProperty(
                  shapeLayerId,
                  'line-opacity',
                  defaultOpacity,
                );

                if (shapeType === 'dashed-line') {
                  map.setPaintProperty(
                    `${shapeLayerId}-main-line`,
                    'line-opacity',
                    shapeConfig.defaultOpacity || 0.7,
                  );
                  map.setPaintProperty(
                    `${shapeLayerId}-main-line`,
                    'line-dasharray',
                    shapeConfig.dasharray || [1.5, 2],
                  );
                }
              }
            });

            // unhover polygon
            map.on('mouseleave', `${shapeLayerId}-polygon`, (e) => {
              if (activeConsoleLogs) console.log('mouseleave polygon', value);
              if (skipInteraction) return;
              map.getCanvas().style.cursor = '';
              if (!shapeConfig.disablePolygonHover) {
                map.setPaintProperty(
                  `${shapeLayerId}-polygon`,
                  'fill-opacity',
                  shapeConfig.polygonOpacity || 0.7,
                );
              }
            });
          }
        }); // End of renderableShapes.forEach

        // Add pulsing dots for any project that has them defined
        if (pulsingDotShapes.length > 0) {
          pulsingDotShapes.forEach((dotShape, index) => {
            const dotId = index === 0 ? value : `${value}-${index + 1}`;
            // Use shape's color if defined, otherwise use project color
            const dotColor = dotShape.color || color;
            map.addImage(`pulsing-dot-${dotId}`, createPulsingDot(dotColor), {
              pixelRatio: 2,
            });
            map.addSource(`${dotId}-pulsing-dot-source`, {
              type: 'geojson',
              data: {
                type: 'FeatureCollection',
                features: [
                  {
                    type: 'Feature',
                    geometry: {
                      type: 'Point',
                      coordinates: dotShape.coordinates,
                    },
                  },
                ],
              },
            });
            map.addLayer({
              id: `${dotId}-pulsing-dot-layer`,
              type: 'symbol',
              source: `${dotId}-pulsing-dot-source`,
              layout: { 'icon-image': `pulsing-dot-${dotId}` },
            });

            // Add label for pulsing dot if specified
            if (dotShape.label) {
              const textDiv = document.createElement('div');
              textDiv.className = 'marker-project';
              textDiv.innerHTML = dotShape.label;
              textDiv.style.backgroundColor = 'rgba(255, 87, 30, 0.7019607843)';
              textDiv.style.padding = '2px 5px';
              textDiv.style.fontSize = '10px';
              textDiv.style.color = 'white';
              textDiv.style.zIndex = '1';
              textDiv.style.cursor = 'default';

              const defaultLatLng = [144.9188379078945, -37.82708680519439];
              const futurePumpMarker = new mapboxgl.Marker({
                element: textDiv,
                anchor: 'bottom',
              })
                .setLngLat(defaultLatLng)
                .addTo(map);

              map.on('zoom', () => {
                const zoom = map.getZoom();
                if (zoom > 14.8) {
                  futurePumpMarker.setLngLat([
                    144.91884700899823, -37.82609963753567,
                  ]);
                } else {
                  futurePumpMarker.setLngLat(defaultLatLng);
                }
              });
            }
          });
        }

        if (marker) {
          //marker
          const el = document.createElement('div');
          const titleSplitted = title.split(' - ');
          // Use categoryColor if set, otherwise use color
          const effectiveColor = categoryColor || color;
          el.innerHTML = `<span project="${value}" class="title">
                            ${titleSplitted[0]}
                          </span>
                          ${
                            titleSplitted[1]
                              ? ` <span project="${value}" class="subtitle">
                                      ${titleSplitted[1]}
                                  </span>`
                              : ''
                          }
              `;
          el.setAttribute('project', value);
          el.setAttribute('id', `marker-project-${value}`);
          el.className = `marker-project ${category} ${subcategory} project-${value} color-${effectiveColor}`;

          // popup
          const ComponentContent = marker?.content || null;
          const placeholder = document.createElement('div');
          if (ComponentContent) {
            ReactDOM.render(<ComponentContent />, placeholder);
          }

          const positionPopup =
            !openPopup || openPopup === 'top' ? 'bottom' : 'top';

          const popup = new mapboxgl.Popup({
            closeButton: true,
            anchor: positionPopup,
            className: `popup-${value} open-popup-${positionPopup}`,
          }).setDOMContent(placeholder);

          popup.on('open', () => {
            if (activeConsoleLogs) console.log('popup opened', value);

            if (skipInteraction) return;

            // Update opacity for all shapes in this project
            renderableShapes.forEach((shape, shapeIndex) => {
              const shapeLayerId =
                shapeIndex === 0 ? value : `${value}-shape-${shapeIndex}`;
              const shapeType = shape.type;
              const shapeConfig = shape.config || {};

              // POLYGON PROJECTS
              if (shapeType === 'polygon-with-line') {
                const openOpacity = shapeConfig.openOpacity || 1;
                map.setPaintProperty(
                  `${shapeLayerId}-polygon`,
                  'fill-opacity',
                  openOpacity,
                );
              }

              // LINE PROJECTS: keep line solid + undashed
              if (shapeType === 'dashed-line') {
                map.setPaintProperty(
                  `${shapeLayerId}-main-line`,
                  'line-dasharray',
                  [1],
                );
                map.setPaintProperty(
                  `${shapeLayerId}-main-line`,
                  'line-opacity',
                  1,
                );
              } else if (
                shapeType !== 'polygon-with-line' &&
                shapeType !== 'pulsing-dot' &&
                shape.data
              ) {
                map.setPaintProperty(shapeLayerId, 'line-opacity', 1);
              }
            });

            document.querySelector('body').classList.add('popup-is-open');
            document
              .getElementById(`marker-project-${value}`)
              ?.classList.add('popup-is-open');
          });

          popup.on('close', () => {
            if (activeConsoleLogs) console.log('popup closed', value);
            if (skipInteraction) return;

            // Reset opacity for all shapes in this project
            renderableShapes.forEach((shape, shapeIndex) => {
              const shapeLayerId =
                shapeIndex === 0 ? value : `${value}-shape-${shapeIndex}`;
              const shapeType = shape.type;
              const shapeConfig = shape.config || {};

              // Reset polygon opacity
              if (shapeType === 'polygon-with-line') {
                map.setPaintProperty(
                  `${shapeLayerId}-polygon`,
                  'fill-opacity',
                  shapeConfig.polygonOpacity || 0.7,
                );
              }

              // Reset dashed line projects
              if (shapeType === 'dashed-line') {
                map.setPaintProperty(
                  `${shapeLayerId}-main-line`,
                  'line-opacity',
                  0.5,
                );
                map.setPaintProperty(
                  `${shapeLayerId}-main-line`,
                  'line-dasharray',
                  shapeConfig.dasharray || [1.5, 2],
                );
              } else if (
                shapeType !== 'polygon-with-line' &&
                shapeType !== 'pulsing-dot' &&
                shape.data
              ) {
                // Reset line opacity for simple line projects
                map.setPaintProperty(
                  shapeLayerId,
                  'line-opacity',
                  shapeConfig.defaultOpacity || 0.3,
                );
                map.setPaintProperty(
                  shapeLayerId,
                  'line-dasharray',
                  shapeConfig.dasharray || [1],
                );
              }
            });

            document.querySelector('body').classList.remove('popup-is-open');
            document
              .getElementById(`marker-project-${value}`)
              ?.classList.remove('popup-is-open');
          });

          const marker2 = new mapboxgl.Marker(el)
            .setLngLat(marker.position)
            .setPopup(marker?.content ? popup : null)
            .addTo(map);

          placeholder.className = `${category} ${subcategory} color-${effectiveColor}`;

          // if (marker?.content) {
          marker2.getElement().addEventListener('click', (event) => {
            const project = event.target.attributes['project'].value;
            if (skipInteraction) return;
            onClickMarker(project);

            // Update all shapes for this project
            renderableShapes.forEach((shape, shapeIndex) => {
              const shapeLayerId =
                shapeIndex === 0 ? value : `${value}-shape-${shapeIndex}`;
              const shapeType = shape.type;

              if (shapeType === 'polygon-with-line') {
                map.setPaintProperty(
                  `${shapeLayerId}-polygon`,
                  'fill-opacity',
                  1,
                );
              }
            });

            // Add zoom animation when marker is clicked
            if (zoomOnClick) {
              map.flyTo({
                center: marker.position,
                zoom: zoomOnClick,
              });
            }
          });

          marker2.getElement().addEventListener('mouseenter', (event) => {
            if (activeConsoleLogs) console.log('mouseenter marker', value);
            map.getCanvas().style.cursor = 'pointer';

            // Update all shapes for this project
            renderableShapes.forEach((shape, shapeIndex) => {
              const shapeLayerId =
                shapeIndex === 0 ? value : `${value}-shape-${shapeIndex}`;
              const shapeType = shape.type;
              const shapeConfig = shape.config || {};

              if (shapeType === 'polygon-with-line') {
                const hoverOpacity = shapeConfig.hoverOpacity || 1;
                map.setPaintProperty(
                  `${shapeLayerId}-polygon`,
                  'fill-opacity',
                  hoverOpacity,
                );
              } else if (shape.data && shapeType !== 'pulsing-dot') {
                map.setPaintProperty(shapeLayerId, 'line-opacity', 1);
              }

              // Set solid style on hover — only if popup isn't open
              if (shapeType === 'dashed-line' && !isPopupOpen()) {
                map.setPaintProperty(
                  `${shapeLayerId}-main-line`,
                  'line-dasharray',
                  [1],
                );
                map.setPaintProperty(
                  `${shapeLayerId}-main-line`,
                  'line-opacity',
                  1,
                );
              } else if (
                shapeType !== 'polygon-with-line' &&
                shapeType !== 'pulsing-dot' &&
                shape.data
              ) {
                map.setPaintProperty(shapeLayerId, 'line-opacity', 1);
              }
            });
          });

          marker2.getElement().addEventListener('mouseleave', (event) => {
            if (activeConsoleLogs) console.log('mouseleave marker', value);
            map.getCanvas().style.cursor = '';

            if (!isPopupOpen()) {
              // Reset opacity for all shapes in this project
              renderableShapes.forEach((shape, shapeIndex) => {
                const shapeLayerId =
                  shapeIndex === 0 ? value : `${value}-shape-${shapeIndex}`;
                const shapeType = shape.type;
                const shapeConfig = shape.config || {};

                // polygon reset
                if (shapeType === 'polygon-with-line') {
                  const resetOpacity = shapeConfig.resetOpacity || 0.7;
                  map.setPaintProperty(
                    `${shapeLayerId}-polygon`,
                    'fill-opacity',
                    resetOpacity,
                  );
                } else if (shape.data && shapeType !== 'pulsing-dot') {
                  // line opacity reset
                  const defaultOpacity = shapeConfig.defaultOpacity || 0.3;
                  map.setPaintProperty(
                    shapeLayerId,
                    'line-opacity',
                    defaultOpacity,
                  );
                }

                // Reset dashed style on unhover
                if (shapeType === 'dashed-line') {
                  map.setPaintProperty(
                    `${shapeLayerId}-main-line`,
                    'line-dasharray',
                    shapeConfig.dasharray || [1.5, 2],
                  );
                  map.setPaintProperty(
                    `${shapeLayerId}-main-line`,
                    'line-opacity',
                    shapeConfig.defaultOpacity || 0.7,
                  );
                }
              });
            }
          });
          // }

          const cloneMarkers = markers;
          cloneMarkers.push({ value, marker: marker2 });
          setMarkers(cloneMarkers);
        }
      });
    });
  }, [map]);

  return (
    <section id="mapbox" className={`mapbox`}>
      <div className="map-container" ref={mapContainer} />

      <div className="legends">
        <h4>LEGEND</h4>
        <ul>
          <li>
            <LegendProjectZone color="#ff571e" />
            <span>Sewerage projects</span>
          </li>
          <li>
            <LegendProjectZone color="#002379" />
            <span>Renewals projects</span>
          </li>
          <li>
            <LegendProjectZone color="#8c50b4" />
            <span>Recycled water projects</span>
          </li>
          <li>
            <LegendPumpSM color="#ff571e" />
            <span>Pump station</span>
          </li>
          <li>
            <LegendPumpDingley color="#8c50b4" />
            <span>Pump station</span>
          </li>
          <li>
            <LegendWRP color="#8c50b4" />
            <span>Water recycling plant</span>
          </li>
        </ul>
      </div>
    </section>
  );
};

export default Map;
